import { selectVideoCodec, videoEncoderConfig } from "./video-codecs.js";
import { VIDEO_PACKET_CONFIG, VIDEO_PACKET_FRAME, decodeVideoConfig, decodeVideoFrame, fromBase64, videoConfigPacket, videoFramePacket } from "./video-protocol.js";

export class VideoPublisher {
  constructor({ publishPacket, onError = console.error, onState = () => { } }) {
    this.publishPacket = publishPacket;
    this.onError = onError;
    this.onState = onState;
    this.encoders = new Map();
  }

  async start({ mediaStream, preview, modes }) {
    this.mediaStream = mediaStream;
    this.preview = preview;
    this.encoders = new Map();
    for (const track of modes) {
      await this.addMode(track);
    }
    this.lastKeyFrameAt = 0;
    const cameraTrack = mediaStream.getVideoTracks()[0];
    if (cameraTrack && globalThis.MediaStreamTrackProcessor) {
      const processor = new MediaStreamTrackProcessor({ track: cameraTrack });
      this.cameraFrameReader = processor.readable.getReader();
      this.consumeCameraFrames();
    } else if (typeof preview.requestVideoFrameCallback === "function") {
      this.schedulePreviewCapture();
    } else {
      this.captureTimer = window.setInterval(() => this.capturePreviewFrame(), 1000 / 30);
    }
  }

  /**
   * Добавляет один quality-энкодер. Нужно, чтобы high включать только по запросу.
   * @param {object} track
   */
  async addMode(track) {
    if (!this.mediaStream || this.encoders.has(track.quality)) return undefined;
    const selection = await selectVideoCodec(track, track.preference, track.hardwareOnly);
    if (!selection) throw new Error(`${track.quality} codec preference is not supported on this device.`);
    const support = await VideoEncoder.isConfigSupported(videoEncoderConfig(selection.codec, track, selection.acceleration));
    if (!support.supported) throw new Error(`${selection.codec.label} ${track.quality} encoder is not supported on this device.`);
    const state = {
      ...track, codec: selection.codec, config: support.config,
      canvas: Object.assign(document.createElement("canvas"), { width: track.width, height: track.height }),
      decoderConfig: undefined, pendingAccessUnit: undefined, publishing: false, lastEncodedAt: 0, stopped: false
    };
    const encoder = new VideoEncoder({
      output: (chunk, metadata) => {
        if (metadata?.decoderConfig) state.decoderConfig = metadata.decoderConfig;
        this.queueAccessUnit(state, chunk.type === "key" ? videoConfigPacket(state.decoderConfig, track, selection.codec) : undefined, videoFramePacket(chunk));
      },
      error: error => this.onError(error, track.quality)
    });
    encoder.configure(support.config);
    state.encoder = encoder;
    this.encoders.set(track.quality, state);
    this.emitLabels();
    return `${track.quality}: ${selection.codec.label}${selection.acceleration === "prefer-software" ? " (software)" : ""}`;
  }

  /**
   * Выключает один quality, остальные не трогает.
   * @param {string} quality
   */
  stopMode(quality) {
    const state = this.encoders.get(quality);
    if (!state) return;
    state.stopped = true;
    state.pendingAccessUnit = undefined;
    try {
      state.encoder.close();
    } catch {
      // энкодер уже закрыт
    }
    this.encoders.delete(quality);
    this.emitLabels();
  }

  /** Есть ли этот quality. */
  hasMode(quality) {
    return this.encoders.has(quality);
  }

  emitLabels() {
    const labels = [...this.encoders.values()].map(state => {
      const software = state.config?.hardwareAcceleration === "prefer-software" ? " (software)" : "";
      return `${state.quality}: ${state.codec.label}${software}`;
    });
    this.onState({ labels });
  }

  stop() {
    window.clearInterval(this.captureTimer); this.captureTimer = undefined;
    if (this.captureFrameCallback !== undefined && this.preview?.cancelVideoFrameCallback) this.preview.cancelVideoFrameCallback(this.captureFrameCallback);
    this.captureFrameCallback = undefined;
    if (this.cameraFrameReader) { this.cameraFrameReader.cancel().catch(() => { }); this.cameraFrameReader = undefined; }
    for (const state of this.encoders.values()) { state.stopped = true; state.pendingAccessUnit = undefined; state.encoder.close(); }
    this.encoders.clear(); this.mediaStream = undefined;
  }

  async consumeCameraFrames() {
    try {
      while (this.cameraFrameReader && this.mediaStream) {
        const { done, value: frame } = await this.cameraFrameReader.read();
        if (done) break;
        this.encodeFrame(frame); frame.close();
      }
    } catch (error) {
      if (this.mediaStream) this.onError(error);
    }
  }

  schedulePreviewCapture() {
    this.captureFrameCallback = this.preview.requestVideoFrameCallback(() => {
      this.captureFrameCallback = undefined;
      this.capturePreviewFrame();
      if (this.mediaStream && this.encoders.size) this.schedulePreviewCapture();
    });
  }

  capturePreviewFrame() {
    if (!this.mediaStream || this.preview.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return;
    const frame = new VideoFrame(this.preview, { timestamp: Math.round(performance.now() * 1000) });
    this.encodeFrame(frame); frame.close();
  }

  encodeFrame(sourceFrame) {
    if (!this.mediaStream) return;
    const timestamp = Date.now() * 1000;
    const keyFrame = timestamp - this.lastKeyFrameAt >= 1_000_000;
    if (keyFrame) this.lastKeyFrameAt = timestamp;
    for (const state of this.encoders.values()) {
      if (state.encoder.encodeQueueSize > 2 || timestamp - state.lastEncodedAt < 1_000_000 / state.framerate) continue;
      state.lastEncodedAt = timestamp;
      const frame = sourceFrame.displayWidth === state.width && sourceFrame.displayHeight === state.height
        ? new VideoFrame(sourceFrame, { timestamp })
        : this.resizeFrame(sourceFrame, state, timestamp);
      state.encoder.encode(frame, { keyFrame }); frame.close();
    }
  }

  resizeFrame(sourceFrame, state, timestamp) {
    state.canvas.getContext("2d", { alpha: false }).drawImage(sourceFrame, 0, 0, state.width, state.height);
    return new VideoFrame(state.canvas, { timestamp });
  }

  queueAccessUnit(state, config, frame) {
    state.pendingAccessUnit = { config, frame };
    if (!state.publishing) this.publishLatest(state);
  }

  async publishLatest(state) {
    state.publishing = true;
    try {
      while (state.pendingAccessUnit && !state.stopped && this.mediaStream) {
        const accessUnit = state.pendingAccessUnit; state.pendingAccessUnit = undefined;
        if (accessUnit.config) await this.publishPacket(state.quality, accessUnit.config);
        await this.publishPacket(state.quality, accessUnit.frame);
      }
    } catch (error) {
      this.onError(error, state.quality);
    } finally {
      state.publishing = false;
      if (state.pendingAccessUnit && !state.stopped && this.mediaStream) this.publishLatest(state);
    }
  }
}

export class VideoReceiver {

  constructor({ findTarget, canDraw = () => true, onWarning = console.warn }) {
    this.findTarget = findTarget;
    this.canDraw = canDraw;
    this.onWarning = onWarning;
    this.tracks = new Map();
  }

  receive(channel, payload) {
    if (payload[0] === VIDEO_PACKET_CONFIG) this.configure(channel, decodeVideoConfig(payload));
    else if (payload[0] === VIDEO_PACKET_FRAME) this.decode(channel, payload);
  }

  configure(channel, config) {
    if (!globalThis.VideoDecoder) throw new Error("This browser does not support WebCodecs video decoding.");
    if (!this.findTarget(channel)) return;
    const signature = JSON.stringify(config);
    const existing = this.tracks.get(channel);
    if (existing?.signature === signature && existing.decoder.state !== "closed") return;
    this.close(channel);
    const decoder = new VideoDecoder({
      output: frame => this.render(channel, frame),
      error: error => this.onWarning("[conference] video decoder failed", { channel, error })
    });
    const decoderConfig = { codec: config.codec, codedWidth: config.width, codedHeight: config.height };
    if (config.description) decoderConfig.description = fromBase64(config.description).buffer;
    decoder.configure(decoderConfig);
    this.tracks.set(channel, {
      decoder,
      signature,
      timings: new Map(),
      pendingFrame: undefined,
      pendingTiming: undefined,
      presentFrameId: undefined
    });
  }

  decode(channel, payload) {
    const track = this.tracks.get(channel);
    if (!track?.decoder || track.decoder.state === "closed") return;
    const frame = decodeVideoFrame(payload);
    if (!frame) return;
    // Behind live edge: keep only keyframes until the decoder catches up.
    if (track.decoder.decodeQueueSize > 1 && !frame.keyFrame) return;
    const chunk = new EncodedVideoChunk({ type: frame.keyFrame ? "key" : "delta", timestamp: frame.timestamp, duration: frame.duration, data: frame.data });
    track.timings.set(chunk.timestamp, { encodedAt: frame.encodedAt, receivedAt: Date.now() * 1000 });
    if (track.timings.size > 64) track.timings.delete(track.timings.keys().next().value);
    try { track.decoder.decode(chunk); } catch (error) { this.onWarning("[conference] dropped video packet", { channel, error }); }
  }

  /**
   * Live edge: coalesce decoder output to one paint per screen frame.
   * Older pending frames are dropped instead of queued for playback.
   * @param {string} channel
   * @param {VideoFrame} frame
   */
  render(channel, frame) {
    const target = this.findTarget(channel);
    const track = this.tracks.get(channel);
    if (!target || !track) {
      frame.close();
      return;
    }
    this.enqueuePresent(channel, frame, track.timings.get(frame.timestamp));
  }

  /** Keeps only the newest decoded frame; schedules one rAF paint per channel. */
  enqueuePresent(channel, frame, timing) {
    const track = this.tracks.get(channel);
    if (!track) {
      frame.close();
      return;
    }
    if (track.pendingFrame) track.pendingFrame.close();
    track.pendingFrame = frame;
    track.pendingTiming = timing;
    if (track.presentFrameId !== undefined) return;
    track.presentFrameId = window.requestAnimationFrame(() => {
      track.presentFrameId = undefined;
      this.presentLatest(channel);
    });
  }

  /** Paints the latest coalesced frame for this channel. */
  presentLatest(channel) {
    const track = this.tracks.get(channel);
    if (!track?.pendingFrame) return;
    const frame = track.pendingFrame;
    const timing = track.pendingTiming;
    track.pendingFrame = undefined;
    track.pendingTiming = undefined;
    this.drawFrame(channel, frame, timing);
  }

  drawFrame(channel, frame, timing) {
    try {
      const target = this.findTarget(channel);
      const track = this.tracks.get(channel);
      if (!target || !track || track.decoder.state === "closed") return;
      if (target.participantId && !this.canDraw(target.participantId)) return;
      const { canvas, tile } = target;
      if (canvas.width !== frame.displayWidth || canvas.height !== frame.displayHeight) {
        canvas.width = frame.displayWidth;
        canvas.height = frame.displayHeight;
      }
      canvas.getContext("2d", { alpha: false }).drawImage(frame, 0, 0, canvas.width, canvas.height);
      if (timing) {
        track.timings.delete(frame.timestamp);
        const now = Date.now() * 1000;
        tile.title = `video: encode ${Math.round((timing.encodedAt - frame.timestamp) / 1000)} ms · transport ${Math.round((timing.receivedAt - timing.encodedAt) / 1000)} ms · decode ${Math.round((now - timing.receivedAt) / 1000)} ms`;
      }
    } finally {
      frame.close();
    }
  }

  close(channel) {
    const track = this.tracks.get(channel);
    if (!track) return;
    if (track.presentFrameId !== undefined) {
      window.cancelAnimationFrame(track.presentFrameId);
    }
    if (track.pendingFrame) track.pendingFrame.close();
    this.tracks.delete(channel);
    if (track.decoder.state === "closed") return;
    try {
      track.decoder.close();
    } catch (error) {
      this.onWarning("[conference] video decoder close failed", { channel, error });
    }
  }

  closeAll() {
    for (const channel of [...this.tracks.keys()]) this.close(channel);
  }
}
